import dagre from 'dagre';
import produce from 'immer';

import {ProcessFlowchartNodeDetailsType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  Flowchart,
  FlowchartBaseNode,
  FlowchartBaseEdge,
} from 'processes/graph-builder/flowchart-viz/FlowchartVizConstants';
import {
  AUTOLAYOUT_NODE_PADDING,
  PROCESS_FLOWCHART_EDGE_LABEL_SIZE,
  PROCESS_FLOWCHART_EDGE_LABEL_WEIGHT,
} from 'processes/graph-builder-processes/flowchart-viz/ProcessFlowchartVizConstants';
import maybeEllipsizeLabel from 'processes/graph-builder-processes/flowchart-viz/util/maybeEllipsizeLabel';
import {FONT_FAMILY_PROXIMA} from 'styles/FontConstants';
import {getTextDimensions} from 'util/getTextDimensions';
import * as logging from 'util/Logging';

function getDagreGraphLayoutOptions(padding: number): {rankdir: string; marginy: number} {
  return {
    // Dagre supports the direction ( TB = top to bottom, BT = bottom to top, RL = right to left, LR = left to right)
    // Here we hardcode the graph direction to "top to bottom".
    // https://github.com/dagrejs/dagre/wiki#configuring-the-layout
    rankdir: 'TB',
    marginy: padding,
  };
}

interface SizingOptions {
  defaultNodeWidth: number;
  defaultNodeHeight: number;
  flowchartEditorPadding: number;
}

export interface EdgeClientIdToLabelDictionary {
  [key: string]: string | null;
}

function getNodesAndEdgesWithDefaultPosition<
  TNode extends FlowchartBaseNode,
  TEdge extends FlowchartBaseEdge,
>(
  nodes: Array<TNode>,
  edges: Array<TEdge>,
  edgeClientIdToLabelDictionary: EdgeClientIdToLabelDictionary,
  sizingOptions: SizingOptions
): {
  nodesWithPositions: Array<TNode>;
  edgesWithPositions: Array<TEdge>;
} {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph(getDagreGraphLayoutOptions(sizingOptions.flowchartEditorPadding));

  const getNodeWidth = (node: TNode): number => node.instanceData.width;
  const getNodeHeight = (node: TNode): number => node.instanceData.height;
  const getNodeNameHeight = (node: TNode): number | undefined => node.instanceData.nameHeight;

  for (const node of nodes) {
    // For some nodes we render its name with `position: 'absolute'` and a negative `top`
    // We don't want the edge to be drawn through this name, so we tell dagre that the node is actually
    // taller than it is:
    //                                | incoming edge
    //                                v
    //                        ------------------
    //       NAME_OFFSET_PX ↕ |  namegoeshere  |
    //                        xxxxxxxxxxxxxxxxxx
    //                        x                x
    //       NODE_HEIGHT_PX ↕ x   actualnode   x
    //                        x                x
    //                        xxxxxxxxxxxxxxxxxx
    //
    // Dagre will give the node extra vertical space, so the edges will not intersect with the name.
    // Later: dagre will give us an (x,y) position for this node, which is the center of the node.
    // We'll have to account for this offset when positioning the node as well.
    //
    // Similarly, for nodes without floating edges, we will still tell autolayout that the nodes are slightly
    // taller than they are. This is to make sure the arrows at the end of the edge don't clip against the node.
    // We will not account for this offset when positioning the node because we want a real buffer

    // Note: when drawing edges that are not computed from autolayout (dynamic), the name offset is
    // handled in the actual edge computation function getEdgeRenderingData

    let autolayoutFakeHeight = getNodeHeight(node);
    const nodeWidth = getNodeWidth(node);
    autolayoutFakeHeight += getNodeNameHeight(node) ?? AUTOLAYOUT_NODE_PADDING;

    dagreGraph.setNode(node.clientId, {
      width: nodeWidth,
      height: autolayoutFakeHeight,
    });
  }

  const rootNode = nodes.find((n) => n.type === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS);
  for (const edge of edges) {
    const edgeLabel = edgeClientIdToLabelDictionary[edge.clientId];

    // This strongly encourages dagre to bring direct descendents of the root to the first rank (row) of
    // the graph. Without this dagre may cluster these nodes to much lower. Consider:
    //
    //     Without weights.                   With weights on (just)
    //  Dagre tries to cluster                   root edges
    //
    //       Root                                  Root
    //       / \                                   / \
    //      A   |                                 A   D
    //      |   |                                 |   |\
    //      B   |                                 B   | |
    //      |   |                                 |   | |
    //      C   |                                 C   | |
    //      |   |                                 |   | |
    //      |   D                                 |   | |
    //       \  | \                                \  | |
    //        \ |  |                                \ | |
    //          E  F                                  E F
    //
    // Note that this is just a design call we made because we decided this made the graphs easier
    // to read.
    const edgeWeight = rootNode && edge.fromNodeClientId === rootNode.clientId ? 100 : 1;

    if (edgeLabel) {
      const clippedLabel = maybeEllipsizeLabel(edgeLabel);
      const textDimensions = getTextDimensions(
        clippedLabel,
        PROCESS_FLOWCHART_EDGE_LABEL_WEIGHT,
        PROCESS_FLOWCHART_EDGE_LABEL_SIZE,
        FONT_FAMILY_PROXIMA
      );

      dagreGraph.setEdge(edge.fromNodeClientId, edge.toNodeClientId, {
        label: clippedLabel,
        width: textDimensions.width,
        height: textDimensions.height,
        labelpos: 'c',
        weight: edgeWeight,
      });
    } else {
      // If there is no label we'll still set a dummy label to let autolayout find a good
      // point for us to render the interaction buttons in the edge renderer
      dagreGraph.setEdge(edge.fromNodeClientId, edge.toNodeClientId, {
        label: ' ',
        width: 13, // actual button sizes are defined in CSS, so this is an unfortunate hardcode
        height: 13,
        labelpos: 'c',
        weight: edgeWeight,
      });
    }
  }

  dagre.layout(dagreGraph);
  const nodesWithPositions = nodes.map((node) =>
    produce(node, (draftNode: TNode) => {
      const nodeWithPosition = dagreGraph.node(draftNode.clientId);
      const nodeWidth = getNodeWidth(draftNode);
      const nodeHeight = getNodeHeight(draftNode);
      const nameHeight = getNodeNameHeight(draftNode);

      let nodeY = nodeWithPosition.y - nodeHeight / 2;
      nodeY -= (nameHeight ?? 0) / 2;

      const position = {
        x: nodeWithPosition.x - nodeWidth / 2,
        y: nodeY,
      };
      if (draftNode.layout == null) {
        draftNode.layout = {
          position,
        };
      } else {
        draftNode.layout.position = position;
      }
    })
  );

  let edgesWithPositions = [];
  edgesWithPositions = edges.map((edge) =>
    produce(edge, (draftEdge: TEdge) => {
      const edgeWithLayout = dagreGraph.edge(edge.fromNodeClientId, edge.toNodeClientId);
      if (edgeWithLayout) {
        draftEdge.layout = {
          points: edgeWithLayout.points,
          labelWidth: edgeWithLayout.width,
          labelHeight: edgeWithLayout.height,
          labelX: edgeWithLayout.x,
          labelY: edgeWithLayout.y,
        };
      }
    })
  );

  return {
    nodesWithPositions,
    edgesWithPositions,
  };
}

/**
 * Computes the positions for the flowchart based on the dagre layouting engine.
 * https://github.com/dagrejs/dagre/wiki
 */
export default function computeDefaultPositionsForFlowchart<
  TNode extends FlowchartBaseNode,
  TEdge extends FlowchartBaseEdge,
>(
  flowchart: Flowchart<TNode, TEdge>,
  edgeClientIdToLabelDictionary: EdgeClientIdToLabelDictionary,
  sizingOptions: SizingOptions
): Flowchart<TNode, TEdge> {
  let nodesWithPositions: Array<TNode>;
  let edgesWithPositions: Array<TEdge>;

  try {
    ({nodesWithPositions, edgesWithPositions} = getNodesAndEdgesWithDefaultPosition(
      flowchart.nodes,
      flowchart.edges,
      edgeClientIdToLabelDictionary,
      sizingOptions
    ));
    // eslint-disable-next-line benchlint/always-throw-from-catch
  } catch (error) {
    logging.warn('Dagre computation error', {extra: {flowchart, error}});
    return flowchart;
  }
  return {...flowchart, nodes: nodesWithPositions, edges: edgesWithPositions};
}
